import { ButtonV2, Spacing, Text } from "."
import styled from "@emotion/native"
import CloseIcon from "../assets/close-icon.svg"
import RefundedIcon from "../assets/refunded-icon.svg"
import BigRefundedIcon from "../assets/refunded-icon-big.svg"
import UnionIcon from "../assets/Union.svg"
import { ActivityIndicator, Platform } from "react-native"
import { ScrollView } from "react-native-gesture-handler"
import { useCurrencyFormatter, useFeatureFlag } from "../hooks"
import { useTheme } from "@emotion/react"
import { useState } from "react"
import fileService from "../utils/fileService"
import { useTransactionTransferAttachment } from "../api/queries/useTransactionTransferAttachment"
import { StackNavigationProp } from "@react-navigation/stack"
import { ReimbursementStackParamList } from "../router/types"
import { RouteProp } from "@react-navigation/native"
import {
  Attachment,
  useTransactionTransferDetails,
} from "../api/queries/useTransactionTransferDetails"
import { ErrorDialog } from "./ErrorDialog/ErrorDialog"
import { getAddressString } from "../utils/address.ts"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import { LinearGradientWrapper } from "./Onboarding/LinearGradientWrapper"

type TransferDetailsSheetNavigationProp = StackNavigationProp<
  ReimbursementStackParamList,
  "TransferDetails"
>
type TransferDetailsSheetRouterProp = RouteProp<ReimbursementStackParamList, "TransferDetails">

interface TransferDetailsSheetProps {
  navigation: TransferDetailsSheetNavigationProp
  route: TransferDetailsSheetRouterProp
}

export function TransferDetailsSheet({ navigation, route }: TransferDetailsSheetProps) {
  const { transferId } = route.params

  const { data: transfer, isFetching } = useTransactionTransferDetails({
    transferId,
  })
  const reimbursementsEnabled = useFeatureFlag("reimbursements")

  const { formatAmount } = useCurrencyFormatter()
  const [documentOpening, setDocumentOpening] = useState(false)
  const { saveFile, openFile } = fileService()
  const theme = useTheme()
  const { fetch: fetchAttachment } = useTransactionTransferAttachment()
  const insets = useSafeAreaInsets()

  const address = transfer?.recipientAddress
  const addressLine = getAddressString(address)

  const paidDate = transfer?.date
    ? new Date(transfer?.date).toLocaleString("en-US", {
        month: "numeric",
        day: "numeric",
        hour: "numeric",
        minute: "numeric",
      })
    : ""

  const handleDocumentDownload = async (attachment: Attachment) => {
    if (!transfer) {
      return
    }
    setDocumentOpening(true)
    const response = await fetchAttachment(transfer?.externalTransferId, attachment.id)
    const filePath = await saveFile(attachment.name, response.fileContent)
    openFile(filePath, () => setDocumentOpening(false))
  }

  function renderReimbursementStatus() {
    // TODO remove me soon :D
    if (!reimbursementsEnabled) {
      return null
    }
    if (isFetching) {
      return <ActivityIndicator size="large" />
    }

    if (transfer?.isReimbursed) {
      return (
        <Card>
          <RefundedIcon color={theme.iconsColor.refunded} />
          <Spacing right={18} />
          <Text type="sectionTitle">
            You've been reimbursed for {transfer ? formatAmount(transfer?.amount) : ""}!
          </Text>
        </Card>
      )
    }

    if (transfer?.appliedForReimbursement) {
      return (
        <PendingReimbursementContainer>
          <RefundedIcon color={theme.iconsColor.refunded} />
          <Spacing right={8} />
          <Text type="sectionTitle" color="white">
            You’ve applied for reimbursement!
          </Text>
        </PendingReimbursementContainer>
      )
    }

    return (
      <RefundButtonContainer>
        <UnionIcon style={{ zIndex: 2 }} />
        <RefundButtonOuterLayer
          onPress={() => navigation.navigate("LinkYourInsurance", { transferId })}
        >
          <BigRefundedIcon />
        </RefundButtonOuterLayer>
      </RefundButtonContainer>
    )
  }

  return (
    <LinearGradientWrapper colors={["#ADEBBC", "#FFFFFF"]} gradientEnd={0.5}>
      <Wrapper
        additionalPaddingTop={Platform.OS === "android" ? insets.top : 0}
        additionalPaddingBottom={insets.bottom}
      >
        <ScrollView>
          <HeaderContainer>
            <CloseIconContainer onPress={() => navigation.pop()}>
              <CloseIcon color="#29252D" />
            </CloseIconContainer>
          </HeaderContainer>
          <HeroContainer>
            <Text fontSize={20} lineHeight={20} color="gray900" fontFamily="IBMPlexSans-Medium">
              {transfer?.recipientName}
            </Text>
            <Spacing top={5} />
            <Text fontSize={36} lineHeight={43} fontFamily="IBMPlexSans-SemiBold" color="gray900">
              {transfer ? formatAmount(transfer?.amount) : ""}
            </Text>
            <Spacing top={26} />
            {renderReimbursementStatus()}
            <Spacing top={20} />
          </HeroContainer>
          <TransferDetailsContainer>
            <Text type="h2">Payment details</Text>
            <DetailLine>
              <Text
                fontSize={18}
                lineHeight={29}
                color="grayscale700"
                fontFamily="IBMPlexSans-Regular"
              >
                Recipient
              </Text>
              <Text type="body2">{transfer?.recipientName}</Text>
            </DetailLine>
            <DetailLine>
              <Text
                fontSize={18}
                lineHeight={29}
                color="grayscale700"
                fontFamily="IBMPlexSans-Regular"
              >
                Address
              </Text>
              <Text
                fontSize={18}
                lineHeight={29}
                color="grayscale900"
                fontFamily="IBMPlexSans-Regular"
              >
                {addressLine}
              </Text>
            </DetailLine>
            <DetailLine>
              <Text
                fontSize={18}
                lineHeight={29}
                color="grayscale700"
                fontFamily="IBMPlexSans-Regular"
              >
                Paid on
              </Text>
              <Text
                fontSize={18}
                lineHeight={29}
                color="grayscale900"
                fontFamily="IBMPlexSans-Regular"
              >
                {paidDate}
              </Text>
            </DetailLine>
            <DetailLine>
              <Text
                fontSize={18}
                lineHeight={29}
                color="grayscale700"
                fontFamily="IBMPlexSans-Regular"
              >
                Receipt
              </Text>
              <DocumentsWrapper>
                {transfer?.attachments?.map((attachment) =>
                  documentOpening ? (
                    <ActivityIndicator size="small" key={attachment.id + "loading"} />
                  ) : (
                    <Text
                      fontSize={18}
                      lineHeight={29}
                      color="royalBlue"
                      fontFamily="IBMPlexSans-Regular"
                      key={attachment.id}
                      onPress={() => handleDocumentDownload(attachment)}
                      disabled={documentOpening}
                    >
                      {attachment.name}
                    </Text>
                  )
                )}
              </DocumentsWrapper>
            </DetailLine>
          </TransferDetailsContainer>
          <ButtonsWrapper>
            <ButtonV2
              variant="blue"
              customBackgroundColor="spring100"
              title="View All Payments"
              textColor="gray900"
              onPress={() => navigation.pop()}
            />
          </ButtonsWrapper>
        </ScrollView>
        <ErrorDialog />
      </Wrapper>
    </LinearGradientWrapper>
  )
}

const Wrapper = styled.View<{ additionalPaddingTop: number; additionalPaddingBottom: number }>`
  padding-top: ${({ additionalPaddingTop }) => `${additionalPaddingTop}px`};
  padding-bottom: ${({ additionalPaddingBottom }) => `${additionalPaddingBottom}px`};
`

const HeaderContainer = styled.View`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  padding: 20px;
`

const HeroContainer = styled.View`
  padding: 20px 20px 0 20px;
  align-items: center;
`

const TransferDetailsContainer = styled.View`
  padding: 28px 20px 20px 20px;
  flex-grow: 1;
`

const Card = styled.View`
  align-items: center;
  display: flex;
  flex-direction: row;
  border-radius: 20px;
  padding: 23px;
  border-color: ${({ theme }) => theme.card.borderColor};
  border-width: 1px;
`

const PendingReimbursementContainer = styled.View`
  align-items: center;
  border-radius: 100px;
  padding: 12px 20px 12px 20px;
  flex-direction: row;
  border-width: 1px;
  border-color: ${({ theme }) => theme.transferDetailsScreen.pendingReimbursementBorderColor};
`

const DetailLine = styled.View`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  border-bottom-width: 2px;
  border-bottom-color: ${({ theme }) => theme.transferDetailsScreen.detailLine};
  padding: 20px 0px;
`

const ButtonsWrapper = styled.View`
  padding: 20px;
`

const RefundButtonContainer = styled.View`
  align-items: center;
`

const RefundButtonOuterLayer = styled.Pressable`
  width: 100px;
  height: 100px;
  border-radius: 100px;
  background-color: ${({ theme }) => theme.transferDetailsScreen.refundButtonOuterColor};
  top: -5px;
  align-items: center;
  justify-content: center;
`

const CloseIconContainer = styled.Pressable`
  width: 46px;
  height: 46px;
  border-radius: 100px;
  background-color: ${({ theme }) => theme.transferDetailsScreen.closeIconContainer};
  align-items: center;
  justify-content: center;
`

const DocumentsWrapper = styled.View`
  gap: 4px;
`
